import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, CAD } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { RefreshCw, TrendingUp } from "lucide-react";

export default function ImmigrationCEODashboard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { const r = await api.get("/immigration/ceo-dashboard"); setD(r.data); };
  useEffect(() => { load(); }, []);

  const runAlerts = async () => {
    setBusy(true);
    try { const r = await api.post("/immigration/alerts/refresh-now"); toast.success(`Auto-alerts refreshed · ${r.data.tasks_created} task(s) created`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!d) return <div className="text-slate-500">Loading…</div>;

  const kpi = [
    ["New Clients This Month", d.kpis.new_clients_this_month, "text-blue-600"],
    ["Active Files", d.kpis.active_files, "text-slate-900"],
    ["Submitted This Month", d.kpis.submitted_this_month, "text-teal-600"],
    ["Approved This Month", d.kpis.approved_this_month, "text-emerald-600"],
    ["Refused This Month", d.kpis.refused_this_month, "text-rose-600"],
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading-font text-3xl font-bold text-slate-900">Immigration CEO Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Snapshot for {d.month}</p>
        </div>
        <Button data-testid="imm-refresh-alerts" variant="outline" onClick={runAlerts} disabled={busy}><RefreshCw className={`w-4 h-4 mr-2 ${busy?"animate-spin":""}`}/>Refresh Auto-Alerts</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpi.map(([label, val, cls]) => (
          <div key={label} className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="text-xs uppercase text-slate-500">{label}</div>
            <div className={`heading-font text-2xl font-bold mt-1 ${cls}`}>{val}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card title="Financials">
          <Row label="Total Professional Fees" value={CAD(d.financials.total_fees)} />
          <Row label="Total Paid" value={CAD(d.financials.total_paid)} />
          <Row label="Outstanding" value={CAD(d.financials.outstanding)} accent="text-rose-600" />
        </Card>
        <Card title="Files by Status">
          {d.by_status.map(s => <Row key={s.name} label={s.name} value={s.count} />)}
        </Card>
        <Card title="Files by Application Type">
          {d.by_type.slice(0, 12).map(t => <Row key={t.name} label={t.name} value={t.count} />)}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 heading-font font-semibold">Upcoming Status Expiries</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr><th className="text-left px-4 py-2">ID</th><th className="text-left px-4 py-2">Type</th><th className="text-left px-4 py-2">Officer</th><th className="text-right px-4 py-2">Expires</th><th className="text-right px-4 py-2">Days</th></tr>
            </thead>
            <tbody>
              {d.upcoming_expiries.map(x => (
                <tr key={x.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 mono text-xs"><Link className="text-orange-600 hover:underline" to={`/immigration/applications/${x.id}`}>{x.application_number}</Link></td>
                  <td className="px-4 py-2">{x.app_type_name}</td>
                  <td className="px-4 py-2 text-slate-600">{x.officer_name}</td>
                  <td className="px-4 py-2 text-right text-xs">{fmtDate(x.expires)}</td>
                  <td className={`px-4 py-2 text-right font-semibold ${x.days<=7?"text-rose-600":x.days<=14?"text-amber-600":"text-slate-700"}`}>{x.days}</td>
                </tr>
              ))}
              {d.upcoming_expiries.length===0 && <tr><td colSpan={5} className="text-center py-6 text-slate-500">No upcoming expiries.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 heading-font font-semibold">Document Request Deadlines</div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr><th className="text-left px-4 py-2">ID</th><th className="text-left px-4 py-2">Officer</th><th className="text-right px-4 py-2">Deadline</th><th className="text-right px-4 py-2">Days</th></tr>
            </thead>
            <tbody>
              {d.document_deadlines.map(x => (
                <tr key={x.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 mono text-xs"><Link className="text-orange-600 hover:underline" to={`/immigration/applications/${x.id}`}>{x.application_number}</Link></td>
                  <td className="px-4 py-2 text-slate-600">{x.officer_name}</td>
                  <td className="px-4 py-2 text-right text-xs">{fmtDate(x.deadline)}</td>
                  <td className={`px-4 py-2 text-right font-semibold ${x.days<=3?"text-rose-600":x.days<=7?"text-amber-600":"text-slate-700"}`}>{x.days}</td>
                </tr>
              ))}
              {d.document_deadlines.length===0 && <tr><td colSpan={4} className="text-center py-6 text-slate-500">Nothing due soon.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 heading-font font-semibold flex items-center justify-between">
          <span>Files by Officer</span>
          <Link to="/immigration/performance" className="text-sm text-orange-600 hover:underline inline-flex items-center gap-1"><TrendingUp className="w-4 h-4"/>Full Performance</Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left px-4 py-2">Officer</th><th className="text-right px-4 py-2">Active</th><th className="text-right px-4 py-2">Total</th></tr></thead>
          <tbody>
            {d.by_officer.map(o => (
              <tr key={o.officer_id} className="border-t border-slate-100"><td className="px-4 py-2 font-medium">{o.officer_name}</td><td className="px-4 py-2 text-right">{o.active}</td><td className="px-4 py-2 text-right">{o.total}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="heading-font font-semibold text-slate-800 mb-2">{title}</div>
    <div className="divide-y divide-slate-100">{children}</div>
  </div>
);
const Row = ({ label, value, accent }) => (
  <div className="flex items-center justify-between py-2 text-sm">
    <span className="text-slate-600">{label}</span>
    <span className={`font-semibold ${accent||"text-slate-900"}`}>{value}</span>
  </div>
);
